/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/shad/select";
import QRCode from "react-qr-code";
import { Label, textVariants } from "@/src/components/Text/Text";
import { useWalletAddresses } from "@/src/hooks/useWalletAdresses";
import { cn } from "@/lib/utils";
import copyIcon from "@assets/icons/Copy.svg";
import { copy, getUniqueArrayByKey, isAddressCaseSensitive } from "@/src/utils";
import DepositSettings from "./DepositSettings";
import { CheckEnablers } from "@/src/hooks/check-enablers";
import Convert from "./Convert";
import { useTranslation } from "react-i18next";
import { Combobox } from "@/src/components/combobox";
import { MiniLoader } from "@/src/components/Loader";
import { TOptions } from "i18next";

const Deposit = () => {
  const [coin, setCoin] = useState<number | null>(null);
  const [network, setNetwork] = useState<number | null>(null);
  const [address, setAddress] = useState("");
  const { addresses, isLoading } = useWalletAddresses();
  const { data } = CheckEnablers("deposit_mode");
  const { t } = useTranslation();
  const trans = (key: string, options?: TOptions) =>
    t(`app.wallet.deposit.${key}`, options);

  const coinData = addresses?.info;

  const singleCrypto =
    coinData?.find((item) => item.id === coin) ?? coinData?.[0];

  const tickerInstance = coinData?.filter(
    (item) => item?.ticker === singleCrypto?.ticker
  );

  const uniqueCoins =
    coinData &&
    getUniqueArrayByKey<WalletAddressInfo>(coinData as [], "ticker");

  const networkAddress = tickerInstance?.find((item) => item?.id === network)
    ?.addresses[0]?.address;

  useEffect(() => {
    if (
      singleCrypto &&
      singleCrypto.addresses.length <= 1 &&
      tickerInstance?.length === 1
    ) {
      setAddress(singleCrypto.addresses[0].address);
    } else {
      setAddress("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleCrypto]);

  useEffect(
    function setAddressBasedOnNetwork() {
      if (tickerInstance && tickerInstance?.length > 1) {
        setAddress(networkAddress as string);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [network]
  );

  useEffect(
    function resetNetworkWhenCoinIsSelected() {
      if (coin) {
        setNetwork(null);
      }
    },
    [coin]
  );

  const foundCoin = coinData?.find(
    (item) => item?.id === (network !== null ? network : coin)
  );

  if (isLoading) return <MiniLoader />;

  return (
    <div className="grid gap-3">
      <DepositSettings />
      {data?.info !== "convert" ? (
        <div className="grid gap-3 mt-5">
          {uniqueCoins && (
            <div>
              <Label>{trans("form.coin.label")}</Label>

              <Combobox
                items={uniqueCoins?.map((coin) => {
                  return {
                    searchText: coin?.label,
                    render: (
                      <div
                        key={coin?.id}
                        className="flex items-center gap-2   disabled:cursor-not-allowed "
                      >
                        <img
                          className="h-6 w-6 "
                          src={coin?.image}
                          alt="crypto"
                        />
                        <div className="w-full grid">
                          <div className="grid gap-1">
                            <span className="font-semibold text-base ">
                              {coin?.label}
                            </span>
                          </div>
                          {/* <div className="grid">
                            <span className="font-semibold text-xs">
                              {coin?.addresses[0]?.spot_balance}{" "}
                            </span>
                          </div> */}
                        </div>
                      </div>
                    ),
                    value: coin?.id?.toString(),
                  };
                })}
                emptyMessage="Nothing found."
                onChange={(val) => setCoin(Number(val))}
                triggerClassName="h-14 rounded-xl w-full "
                defaultValue={singleCrypto?.id?.toString()}
              />
            </div>
          )}
          {tickerInstance && tickerInstance?.length > 1 && (
            <div>
              <Label>{trans("form.network.label")}</Label>
              <Combobox
                triggerClassName="h-16 rounded-xl"
                items={tickerInstance?.map((item) => ({
                  value: item?.id.toString(),
                  render: (
                    <div
                      key={item?.id}
                      className="flex items-center justify-between w-full "
                    >
                      <div className="flex items-center gap-2">
                        <img
                          src={item?.network_image}
                          alt={item?.ticker}
                          className="h-6 w-6"
                        />
                        <div className="grid">
                          <span>{`${item?.label} ${item?.network_label}`}</span>
                          <span className={textVariants({ size: "xs" })}>
                            {item?.description}
                          </span>
                        </div>
                      </div>
                      <div>
                        {item?.recommended && (
                          <span className="px-2 py-0.5 rounded-2xl bg-indigo-200 text-indigo-400 text-xs">
                            Recommended
                          </span>
                        )}
                      </div>
                    </div>
                  ),
                  searchText: item?.ticker + item?.network_label,
                }))}
                emptyMessage="No items found."
                placeholder={trans("form.network.placeholder")}
                onChange={(val) => setNetwork(Number(val))}
              />
            </div>
          )}
          {tickerInstance && tickerInstance?.length <= 1 && (
            <div>
              <Label>{trans("form.network.label")}</Label>
              <Select
                disabled={
                  (singleCrypto && singleCrypto?.addresses.length <= 1) ||
                  !singleCrypto
                }
                onValueChange={setAddress}
              >
                <SelectTrigger className="w-full capitalize px-3 py-7 rounded-xl">
                  <SelectValue
                    placeholder={trans("form.network.placeholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {singleCrypto?.addresses.map((address, index) => (
                    <SelectItem
                      key={index}
                      className="capitalize"
                      value={address.address ?? ""}
                    >
                      {address?.type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <>
            <div>
              <Label>{trans("form.address.label")}</Label>
            </div>
            {address && (
              <div className="flex items-center flex-col  md:flex-row gap-6 p-4 bg-gray-100 dark:bg-grey-600 rounded-md w-full">
                <QRCode className="h-24 w-24" value={address ?? ""} />
                <div className="flex justify-between w-full items-center">
                  <div className="grid gap-2">
                    <span
                      className={textVariants({
                        case: "capitalize",
                        font: "semibold",
                      })}
                    >
                      {singleCrypto?.label}
                    </span>
                    <span
                      className={cn(textVariants({ size: "sm" }), " truncate")}
                    >
                      {address ?? ""}
                    </span>
                    <div
                      className={cn(
                        textVariants({ size: "sm" }),
                        "text-red-500"
                      )}
                    >
                      {isAddressCaseSensitive(
                        foundCoin?.network ?? "",
                        trans("form.caseSensitive.desc", {
                          coin: foundCoin?.network?.toUpperCase(),
                        })
                      )}
                    </div>
                  </div>
                  <div>
                    <img
                      onClick={() => copy(address ?? "")}
                      src={copyIcon}
                      alt="copy"
                      className="h-5 w-5 cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        </div>
      ) : (
        <Convert />
      )}
    </div>
  );
};

export default Deposit;
